import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { X, Heart, Gift, ArrowRight } from 'lucide-react';

const SupportBanner = () => {
  const [showFull, setShowFull] = useState(false);
  const [showStrip, setShowStrip] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const auth = useAuth() || {};
  const isAuthenticated = auth.isAuthenticated || false;
  const user = auth.user;
  const navigate = useNavigate();
  const isPaid = user?.tier && user.tier !== 'free';

  useEffect(() => {
    // Only show once per session
    if (sessionStorage.getItem('support_banner_shown')) {
      setShowStrip(true);
      return;
    }
    // Show after a short delay
    const timer = setTimeout(() => setShowFull(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setShowFull(false);
    setShowStrip(true);
    setDismissed(true);
    sessionStorage.setItem('support_banner_shown', 'true');
  };

  // Full banner
  if (showFull && !dismissed) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-in fade-in duration-300">
        <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-6 text-white text-center relative">
            <button onClick={dismiss} className="absolute top-3 right-3 text-white/70 hover:text-white">
              <X className="w-5 h-5" />
            </button>
            <Heart className="w-10 h-10 mx-auto mb-3" fill="currentColor" />
            <h2 className="text-xl font-serif font-bold">
              {isPaid ? 'Thank You for Your Support!' : 'Help Us Reach More Children'}
            </h2>
          </div>
          
          {/* Body */}
          <div className="p-6 text-center">
            {isPaid ? (
              <>
                <p className="text-slate-600 mb-4">
                  Your subscription already makes a difference. If you'd like to do even more, 
                  a one-time donation helps us create new books and reach children in schools, 
                  hospitals, and churches who need it most.
                </p>
                <Button onClick={() => { dismiss(); navigate('/donate'); }} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 py-5 w-full">
                  <Gift className="w-4 h-4 mr-2" /> Make a Donation
                </Button>
              </>
            ) : (
              <>
                <p className="text-slate-600 mb-4">
                  Every subscription helps us create free faith-based resources for children 
                  who need them most. Join our community of supporters and unlock the full 
                  library of books, quizzes, and more.
                </p>
                <Button onClick={() => { dismiss(); navigate('/upgrade'); }} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 py-5 w-full mb-3">
                  <Heart className="w-4 h-4 mr-2" /> Become a Supporter — from £4.99/mo
                </Button>
                {!isAuthenticated && (
                  <p className="text-xs text-slate-400">
                    Already a member? <Link to="/login" onClick={dismiss} className="text-emerald-600 underline">Sign in</Link>
                  </p>
                )}
              </>
            )}
            <button onClick={dismiss} className="mt-3 text-sm text-slate-400 hover:text-slate-600">
              Maybe later
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Persistent strip
  if (showStrip) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-emerald-700 text-white py-2 px-4 flex items-center justify-center gap-3 text-sm print:hidden">
        <Heart className="w-4 h-4 flex-shrink-0" fill="currentColor" />
        <span className="hidden sm:inline">
          {isPaid ? 'Your support changes lives.' : 'Support our mission — help children discover faith.'}
        </span>
        <span className="sm:hidden">
          {isPaid ? 'Thank you!' : 'Support our mission'}
        </span>
        <Link
          to={isPaid ? '/donate' : '/upgrade'}
          className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 transition-colors"
        >
          {isPaid ? 'Donate' : 'Learn More'} <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  return null;
};

export default SupportBanner;
